import React, {
  useEffect, useMemo, useRef, forwardRef, useCallback,
} from 'lib/react';
import { useImperativeHandle } from 'react';
import { useTranslation } from 'lib/translation';
import { useDispatch, useSelector } from 'lib/redux';
import {
  updatePaymentInfo, updateCreditCardConsent, expandPanel, clearPaymentInfoError,
} from 'features/finalizeOrder/FinalizeOrder.actions';
import PaymentInformation from 'commonComponents/paymentInformation';
import FinalizeOrderExpansionPanel from '../FinalizeOrderExpansionPanel.component';
import {
  getFooterData, showCardDetails,
} from './PaymentSection.util';
import PaymentSectionFooter from './PaymentSection.Footer.component';
import PaymentSectionHeader from './PaymentSection.Header.component';

const PaymentSection = forwardRef((props, ref) => {
  const paymentInfoRef = useRef(null);
  const latestPaymentInfoRef = useRef(null);
  const {
    paymentInfo, estimatedCostResponse, ccConsent, paymentInfoErrors, isExpanded,
  } = useSelector(({ finalizeOrder, placeOrder }) => ({ ...finalizeOrder, ccConsent: placeOrder.ccConsent }));

  const {
    creditCard, accountBalance, isTermsUser, hasAdministerBillingPermission, isBilledInBulk,
  } = useSelector(({ userData }) => userData?.billingInformation || {});

  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Keep the ref updated with the latest paymentInfo
  useEffect(() => {
    latestPaymentInfoRef.current = paymentInfo;
  }, [paymentInfo]);

  const hasAccountBalance = accountBalance > 0;
  const estimatedTotal = estimatedCostResponse ? estimatedCostResponse.maxTotal : 0;
  const calculatedCreditCardVisibility = useMemo(() => {
    if (!estimatedCostResponse) {
      return false;
    }

    return (
      showCardDetails(hasAdministerBillingPermission, hasAccountBalance, estimatedTotal, accountBalance, isTermsUser)
    );
  }, [estimatedCostResponse, hasAdministerBillingPermission, hasAccountBalance, estimatedTotal, isTermsUser]);

  useEffect(() => {
    dispatch(clearPaymentInfoError());
    if (!creditCard?.creditCardNumber) dispatch(expandPanel('paymentInformation', true));
    else dispatch(expandPanel('paymentInformation', false));
  }, []);

  useImperativeHandle(ref, () => ({
    tokenize: async () => {
      if (!calculatedCreditCardVisibility) {
        return {
          transientToken: null,
          isCardNumberUpdated: false,
          paymentMethod: null,
        };
      }

      if (!paymentInfoRef.current?.tokenize) {
        return {
          transientToken: null,
          isCardNumberUpdated: false,
          paymentMethod: 'card', // #genai
        };
      }

      return paymentInfoRef.current.tokenize();
    },
    setIsEditingCard: (value) => {
      if (paymentInfoRef.current?.setIsEditingCard) {
        paymentInfoRef.current.setIsEditingCard(value);
      }
    },
  }));

  const handleChange = useCallback((fieldId, event) => {
    const currentPaymentInfo = latestPaymentInfoRef.current;
    const { value } = event.target;
    const targetId = fieldId || event.target.id;
    const paymentInformationVal = { ...currentPaymentInfo, isUpdated: true };
    if (targetId === 'cardNumber' && value !== creditCard.creditCardNumber) paymentInformationVal.isCardNumberUpdated = true;
    paymentInformationVal[targetId] = value;
    dispatch(updatePaymentInfo(paymentInformationVal));
  }, []);

  const handleConsentCheckboxChange = () => dispatch(updateCreditCardConsent(!ccConsent));

  const getPanelHeader = () => (
    <PaymentSectionHeader
      creditCardVisibility={calculatedCreditCardVisibility}
      creditCard={creditCard}
    />
  );

  const getFooter = () => {
    const footer = getFooterData(
      hasAdministerBillingPermission,
      isTermsUser,
      isBilledInBulk,
      hasAccountBalance,
      estimatedTotal,
      accountBalance,
      t,
    );
    return (
      <PaymentSectionFooter
        paymentOption={footer.paymentOption}
        planType={footer.planType}
        subText={footer.subText}
        billingMaster={footer.billingMaster}
        boldText={footer.boldText}
      />
    );
  };

  return (
    <FinalizeOrderExpansionPanel
      panelSummary={getPanelHeader()}
      footer={getFooter()}
      expanded={isExpanded.paymentInformation}
      onChange={(_, expanded) => dispatch(expandPanel('paymentInformation', expanded))}
    >
      {calculatedCreditCardVisibility && (
      <PaymentInformation
        ref={paymentInfoRef}
        paymentInfo={paymentInfo}
        handleChange={handleChange}
        handleConsentCheckboxChange={handleConsentCheckboxChange}
        paymentInfoErrors={paymentInfoErrors}
        ccConsent={ccConsent}
        splitCardContainer
        fullWidthInputFields
      />
      )}
    </FinalizeOrderExpansionPanel>
  );
});

export default PaymentSection;
